import { useEffect, useMemo, useRef, useState } from "react";
import { IconChevron } from "./icons";

function Dropdown({
  label,
  options,
  value,
  onChange,
  open,
  onToggle,
  onClose,
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) onClose?.();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, onClose]);

  const selected = options.find((o) => String(o.value) === String(value));

  return (
    <div className="sixinone-dropdown" ref={ref}>
      <button
        type="button"
        className={`filter-dropdown-btn${open ? " is_active" : ""}`}
        onClick={onToggle}
      >
        <span>{selected?.label || label}</span>
        <IconChevron width={14} height={14} />
      </button>
      {open ? (
        <div className="sixinone-dropdown-content">
          {options.map((opt) => (
            <div
              key={String(opt.value)}
              className={`sixinone-dropdown-item${
                String(opt.value) === String(value) ? " is_active" : ""
              }`}
              onClick={() => {
                onChange?.(opt.value);
                onClose?.();
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.66634 1.83398C4.44468 1.83398 1.83301 4.44566 1.83301 7.66732C1.83301 10.889 4.44468 13.5007 7.66634 13.5007C10.888 13.5007 13.4997 10.889 13.4997 7.66732C13.4997 4.44566 10.888 1.83398 7.66634 1.83398ZM0.833008 7.66732C0.833008 3.89337 3.8924 0.833984 7.66634 0.833984C11.4403 0.833984 14.4997 3.89337 14.4997 7.66732C14.4997 9.37433 13.8738 10.9351 12.8389 12.1328L15.0199 14.3138C15.2152 14.509 15.2152 14.8256 15.0199 15.0209C14.8246 15.2161 14.5081 15.2161 14.3128 15.0209L12.1318 12.8399C10.9342 13.8747 9.37335 14.5007 7.66634 14.5007C3.8924 14.5007 0.833008 11.4413 0.833008 7.66732Z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function SportFixtureFilter({
  selectedLeagueIds = [],
  onLeagueChange,
  leagueOptions = [],
  extendValue,
  extendOptions = [],
  onExtendChange,
  bookmakerValue,
  bookmakerOptions = [],
  onBookmakerChange,
}) {
  const [openKey, setOpenKey] = useState(null);
  const [leagueOpen, setLeagueOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [tempIds, setTempIds] = useState([]);
  const leagueRef = useRef(null);

  const allIds = useMemo(
    () => (leagueOptions || []).map((l) => String(l.id)),
    [leagueOptions],
  );

  useEffect(() => {
    if (!leagueOpen) return;
    if (selectedLeagueIds?.length) {
      setTempIds(selectedLeagueIds.map(String));
    } else {
      setTempIds(allIds);
    }
  }, [leagueOpen, allIds, selectedLeagueIds]);

  useEffect(() => {
    if (!leagueOpen) return;
    const onDoc = (e) => {
      if (!leagueRef.current?.contains(e.target)) {
        setLeagueOpen(false);
        setKeyword("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [leagueOpen]);

  const filtered = useMemo(() => {
    const list = leagueOptions || [];
    if (!keyword.trim()) return list;
    const q = keyword.toLowerCase();
    return list.filter((l) => {
      const nameVN = (l.nameVN || "").toLowerCase();
      const name = (l.name || "").toLowerCase();
      const country = (l.country || l.countryName || "").toLowerCase();
      return nameVN.includes(q) || name.includes(q) || country.includes(q);
    });
  }, [leagueOptions, keyword]);

  const toggleTemp = (id) => {
    const sid = String(id);
    setTempIds((prev) => {
      const set = new Set((prev || []).map(String));
      if (set.has(sid)) set.delete(sid);
      else set.add(sid);
      return Array.from(set);
    });
  };

  const isAllSelected =
    allIds.length > 0 &&
    allIds.every((id) => tempIds.map(String).includes(id));

  const isNoneSelected = tempIds.length === 0;

  const closeLeague = () => {
    setLeagueOpen(false);
    setKeyword("");
  };

  const confirmLeague = () => {
    if (isAllSelected || isNoneSelected) {
      onLeagueChange?.([]);
    } else {
      onLeagueChange?.(tempIds.map(String));
    }
    closeLeague();
  };

  const leagueLabel =
    selectedLeagueIds?.length > 0
      ? `Giải đấu (${selectedLeagueIds.length})`
      : "Giải đấu";

  return (
    <div className="fixture-filter">
      <div className="filter-selects">
        <div className="filter-league" ref={leagueRef}>
          <button
            type="button"
            className={`filter-league-btn${
              leagueOpen || selectedLeagueIds?.length ? " is_active" : ""
            }`}
            onClick={() => {
              setOpenKey(null);
              if (leagueOpen) {
                closeLeague();
              } else {
                setLeagueOpen(true);
                setKeyword("");
              }
            }}
          >
            <span>{leagueLabel}</span>
            <IconChevron width={14} height={14} />
          </button>

          {leagueOpen ? (
            <>
              <div className="show_backdrop" onClick={closeLeague} />
              <div
                className="kbv-v31-league-modal league-modal"
                style={{ visibility: "visible" }}
              >
                <div className="leaguebox">
                  <div className="leaguebox-header">
                    <h2>CHỌN GIẢI ĐẤU</h2>
                    <button
                      type="button"
                      className="close-btn"
                      onClick={closeLeague}
                    >
                      ✕
                    </button>
                  </div>

                  <div className="search-row">
                    <div className="search-input-wrap">
                      <span className="search-icon">
                        <SearchIcon />
                      </span>
                      <input
                        type="text"
                        placeholder="Tìm tên giải đấu mà bạn muốn"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="league-list">
                    <div className="league-columns">
                      <div className="list-box">
                        {filtered.map((league) => {
                          const id = String(league.id);
                          const checked = tempIds.map(String).includes(id);
                          const name =
                            league.nameVN || league.name || league.league_name || "";
                          const shortName =
                            league.shortName ||
                            league.nameShort ||
                            name;
                          const logo =
                            league.logo || league.league_logo || "";
                          const count =
                            league.matchCount ??
                            league.count ??
                            league.matchesCount;
                          const hot = league.isHot || league.is_hot;

                          return (
                            <div
                              key={id}
                              className="league-item"
                              onClick={() => toggleTemp(id)}
                            >
                              <div
                                className={`cb${checked ? "" : " unchecked"}`}
                              >
                                {checked ? "✓" : ""}
                              </div>
                              <span
                                className={`league-name${
                                  hot ? " is-hot" : ""
                                }`}
                              >
                                {logo ? (
                                  <img
                                    src={logo}
                                    alt={name}
                                    loading="lazy"
                                    className="league-logo"
                                  />
                                ) : null}
                                <span className="pc">{name}</span>
                                <span className="mb">{shortName}</span>
                                {count != null ? (
                                  <span className="tag">[{count}]</span>
                                ) : null}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="leaguebox-footer">
                    <div
                      className="footer-check-item"
                      onClick={() => setTempIds(allIds)}
                    >
                      <div
                        className={`cb${isAllSelected ? "" : " unchecked"}`}
                      >
                        {isAllSelected ? "✓" : ""}
                      </div>
                      <span>Chọn tất cả</span>
                    </div>
                    <div
                      className="footer-check-item"
                      onClick={() => setTempIds([])}
                    >
                      <div
                        className={`cb${isNoneSelected ? "" : " unchecked"}`}
                      >
                        {isNoneSelected ? "✓" : ""}
                      </div>
                      <span>Bỏ chọn tất cả</span>
                    </div>
                    <button
                      type="button"
                      className="btn-confirm"
                      onClick={confirmLeague}
                    >
                      Xác nhận
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>

        <Dropdown
          label="TL Thập phân"
          options={extendOptions}
          value={extendValue}
          onChange={onExtendChange}
          open={openKey === "extend"}
          onToggle={() => {
            setOpenKey((k) => (k === "extend" ? null : "extend"));
            closeLeague();
          }}
          onClose={() => setOpenKey(null)}
        />

        <Dropdown
          label="Tất cả"
          options={bookmakerOptions}
          value={bookmakerValue}
          onChange={onBookmakerChange}
          open={openKey === "bookmaker"}
          onToggle={() => {
            setOpenKey((k) => (k === "bookmaker" ? null : "bookmaker"));
            closeLeague();
          }}
          onClose={() => setOpenKey(null)}
        />
      </div>
    </div>
  );
}
